export const isEmptyOrUndefinedString = (variable: any) => {
    return ["undefined", undefined, "null", null, ""].includes(variable);
};

export const getValidationMessages = (model) => {
    /**
     * Returns a list of validation messages for each input if the validation
     * is invalid.
     */
    const errors: string[] = [];
    if (!model.$dirty) return errors;
    else {
        if (model.$error) {
            if (model.required && model.required.$invalid) {
                errors.push("This value is required");
            }
            if (model.fileSizeLimit && model.fileSizeLimit.$invalid) {
                errors.push(model.fileSizeLimit.$message);
            }
            if (model.validBirthdate && model.validBirthdate.$invalid) {
                errors.push(model.validBirthdate.$message);
            }
            if (model.minValue && model.minValue.$invalid) {
                errors.push(model.minValue.$message);
            }
            if (model.maxValue && model.maxValue.$invalid) {
                errors.push(model.maxValue.$message);
            }
            if (model.url && model.url.$invalid) {
                errors.push(model.url.$message);
            }
            if (model.maxLength && model.maxLength.$invalid) {
                errors.push(model.maxLength.$message);
            }
            if (model.minLength && model.minLength.$invalid) {
                errors.push(model.minLength.$message);
            }
            if (model.numeric && model.numeric.$invalid) {
                errors.push(model.numeric.$message);
            }
            if (model.alphaNum && model.alphaNum.$invalid) {
                errors.push(model.alphaNum.$message);
            }
            if (model.email && model.email.$invalid) {
                errors.push(model.email.$message);
            }
        }
        return errors;
    }
};

const convertToBytes = (unit: "kB" | "MB" | "GB", limit: number): number => {
    /**
     * Takes a unit (kB, MB, GB) and a size limit as parameters. Based on the unit provided,
     * it multiplies the size limit by the appropriate factor to convert it to bytes. Then,
     * it returns the calculated size limit in bytes.
     * @param {"kB" | "MB" | "GB"} unit
     * @param {number} limit
     * @returns {number}
     */
    switch (unit) {
        case "kB":
            return limit * 1024;
        case "MB":
            return limit * 1024 * 1024;
        case "GB":
            return limit * 1024 * 1024 * 1024;
        default:
            return 0;
    }
};

export const validateFileSize = (
    files: any,
    unit: "kB" | "MB" | "GB",
    sizeLimit: number,
): { valid: boolean; message: string } => {
    /**
     * Validates whether the given file or array of files exceeds a specified size limit.
     * @param {any} files
     * @param {"kB" | "MB" | "GB"} unit
     * @param {number} sizeLimit
     * @returns {{ valid: boolean; message: string }}
     * @example
     * validateFileSize(fileInput.files, "MB", 2);
     */
    if (!files) {
        return { valid: false, message: "No file selected" };
    }

    const fileArray = Array.isArray(files) ? files : [files];

    const limit = convertToBytes(unit, sizeLimit);

    const fileNames: string[] = [];
    let valid = true;

    for (const file of fileArray) {
        if (file.size >= limit) {
            valid = false;
        }
        fileNames.push(file.name);
    }

    const message = valid
        ? ""
        : `The file${fileNames.length > 1 ? "s" : ""} ${fileNames.join(", ")} exceed${fileNames.length > 1 ? "" : "s"} the ${sizeLimit}${unit} individual file size`;

    return {
        valid,
        message,
    };
};

export const validateBirthdate = (
    birthdate: string,
): { valid: boolean; message: string } => {
    /**
     * Validates if the birthdate is in the DD-MM-YYYY format and it's a valid date
     * @param {string} birthdate
     * @returns  { valid: boolean; message: string }
     */
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateRegex.test(birthdate)) {
        return {
            valid: false,
            message: "Invalid date format (DD-MM-YYYY)",
        };
    }

    const [day, month, year] = birthdate.split("-").map(Number);
    /** month - 1 because months are zero-based in JavaScript */
    const date = new Date(year, month - 1, day);

    if (
        date.getDate() !== day ||
        date.getMonth() + 1 !== month ||
        date.getFullYear() !== year
    ) {
        return {
            valid: false,
            message: "Invalid date",
        };
    }

    /** Check if the date is in the past */
    const currentDate = new Date();
    if (date >= currentDate) {
        return {
            valid: false,
            message: "Birthdate must be in the past",
        };
    }

    return {
        valid: true,
        message: "",
    };
};
